/**
 * EventComposer — the panel that makes a block, and the only way to
 * change one after it exists.
 *
 * CAL-EDIT-1. Rennan: *"i also need to be able to give it information.
 * what is the block? a title, information, and also being able to
 * attatch it to a project or client which is optional. also creating a
 * new client and project inside of that."*
 *
 * WHAT WAS THERE BEFORE
 *
 * Drawing a block on the grid called `createCalendarEvent` with
 * `title: 'New block'` — hard-coded, no composer, no way to say what
 * it was. And `EventDetailsModal` has had an `onEdit` prop since #351
 * that NOBODY HAS EVER PASSED, so the Edit button never rendered and
 * no field of an event could be changed at all. Times could be dragged;
 * everything else was set in stone at the moment of the drag. One
 * component fixes both, because creating and editing a block are the
 * same form with a different starting point.
 *
 * NOTHING IS WRITTEN UNTIL SAVE
 *
 * The obvious alternative — insert on drag, then open the composer on
 * the new row, the way Apple Calendar does — is what produced the
 * "New block" rows in the first place. Every abandoned drag leaves one
 * behind, and on a week grid an abandoned drag is common: you misjudge
 * the column, you let go early, you change your mind. So the drag
 * yields a span, the span opens this, and the row appears when the
 * studio says what it is.
 *
 * WHY A PANEL AND NOT A DIALOG
 *
 * It sits beside the grid so the hours stay visible while you name the
 * thing you just drew. Moving a block is the one edit you make by
 * looking, and a modal over the calendar takes that away.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Check, Loader2, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useClients, useCreateClient } from '@/services/clients';
import { useCreateProject, useProjects } from '@/services/projects';
import { useOrganization } from '@/services/organizations';

export interface ComposerDraft {
  /** Present when editing; absent when drawing something new. */
  id?: string;
  title: string;
  description: string;
  startAt: Date;
  endAt: Date;
  allDay: boolean;
  location: string;
  projectId: string | null;
  clientId: string | null;
}

export interface ComposerSave {
  title: string;
  description: string | null;
  start_at: string;
  end_at: string;
  all_day: boolean;
  location: string | null;
  project_id: string | null;
  client_id: string | null;
}

/** `<input type="datetime-local">` wants local wall time, no zone. */
const localInput = (d: Date) => format(d, "yyyy-MM-dd'T'HH:mm");
const localDate = (d: Date) => format(d, 'yyyy-MM-dd');

export default function EventComposer({
  draft,
  onCancel,
  onSave,
  onDelete,
  saving,
}: {
  draft: ComposerDraft | null;
  onCancel: () => void;
  onSave: (values: ComposerSave, id?: string) => Promise<void> | void;
  onDelete?: (id: string) => Promise<void> | void;
  saving?: boolean;
}) {
  const { data: organization } = useOrganization();
  const { data: clients = [] } = useClients();
  const { data: projects = [] } = useProjects();
  const createClient = useCreateClient();
  const createProject = useCreateProject();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [location, setLocation] = useState('');
  const [projectId, setProjectId] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  /** Which link is mid-creation, and what it is being called. */
  const [newKind, setNewKind] = useState<'project' | 'client' | null>(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const titleRef = useRef<HTMLInputElement>(null);
  const key = draft ? (draft.id ?? `new-${draft.startAt.toISOString()}`) : null;

  // Keyed on the draft rather than on `draft` itself: re-running this
  // on every parent render would stamp on what is being typed.
  useEffect(() => {
    if (!draft) return;
    setTitle(draft.title);
    setDescription(draft.description);
    setStart(draft.allDay ? localDate(draft.startAt) : localInput(draft.startAt));
    setEnd(draft.allDay ? localDate(draft.endAt) : localInput(draft.endAt));
    setAllDay(draft.allDay);
    setLocation(draft.location);
    setProjectId(draft.projectId);
    setClientId(draft.clientId);
    setNewKind(null);
    setNewName('');
    // A block you just drew has no name, and naming it is the whole
    // reason this opened.
    const t = window.setTimeout(() => titleRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
    [projects],
  );
  const sortedClients = useMemo(
    () => [...(clients as Array<{ id?: string; name?: string }>)].sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? ''),
    ),
    [clients],
  );

  if (!draft) return null;
  const isEdit = !!draft.id;

  const commitNew = async () => {
    const name = newName.trim();
    if (!name || !organization?.id) return;
    setCreating(true);
    try {
      if (newKind === 'client') {
        const made = await createClient.mutateAsync({ name, org_id: organization.id } as never);
        setClientId((made as { id: string }).id);
      } else {
        const made = await createProject.mutateAsync({ name, org_id: organization.id } as never);
        setProjectId((made as { id: string }).id);
      }
      setNewKind(null);
      setNewName('');
    } catch (err) {
      // The services raise typed plan-cap errors; say what they said.
      toast.error((err as Error)?.message || `Could not create that ${newKind}`);
    } finally {
      setCreating(false);
    }
  };

  const submit = async () => {
    const name = title.trim();
    if (!name) {
      titleRef.current?.focus();
      toast.error('Give it a name first');
      return;
    }
    // An all-day block is a DAY, so it is stored as that day's
    // midnight to the next — the same shape the month grid and the
    // all-day banner already expect from a due date.
    const startAt = allDay ? new Date(`${start}T00:00:00`) : new Date(start);
    let endAt = allDay ? new Date(`${end}T00:00:00`) : new Date(end);
    if (allDay) endAt = new Date(endAt.getTime() + 24 * 60 * 60 * 1000);
    if (!(startAt instanceof Date) || Number.isNaN(startAt.getTime())) {
      toast.error('That start time is not a time');
      return;
    }
    if (endAt <= startAt) {
      // Quietly correct rather than refusing: somebody typing an end
      // before a start has made a typo, not a request.
      endAt = new Date(startAt.getTime() + 60 * 60 * 1000);
    }
    await onSave(
      {
        title: name,
        description: description.trim() || null,
        start_at: startAt.toISOString(),
        end_at: endAt.toISOString(),
        all_day: allDay,
        location: location.trim() || null,
        project_id: projectId,
        client_id: clientId,
      },
      draft.id,
    );
  };

  const linkRow = (
    kind: 'project' | 'client',
    value: string | null,
    setValue: (v: string | null) => void,
    options: Array<{ id?: string; name?: string }>,
  ) => (
    <label style={{ display: 'block' }}>
      <span style={LABEL}>{kind === 'project' ? 'Project' : 'Client'}</span>
      {newKind === kind ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="plan-field"
            autoFocus
            value={newName}
            placeholder={kind === 'project' ? 'New project name' : 'New client name'}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); void commitNew(); }
              if (e.key === 'Escape') { e.preventDefault(); setNewKind(null); setNewName(''); }
            }}
          />
          <button type="button" className="plan-btn" onClick={() => void commitNew()} disabled={creating}>
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          </button>
          <button
            type="button"
            className="plan-btn plan-icon"
            onClick={() => { setNewKind(null); setNewName(''); }}
            aria-label="Cancel"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <select
          className="plan-field"
          value={value ?? ''}
          onChange={(e) => {
            // `__new` is an option rather than a button beside the
            // select so the whole choice is one control: pick the one
            // you want, or pick "New…" and name it. A separate button
            // reads as a second, unrelated thing to do.
            if (e.target.value === '__new') { setNewKind(kind); setNewName(''); return; }
            setValue(e.target.value || null);
          }}
        >
          <option value="">None</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>{o.name || 'Untitled'}</option>
          ))}
          <option value="__new">+ New {kind}…</option>
        </select>
      )}
    </label>
  );

  return (
    <div
      className="plan-panel"
      style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}
      onKeyDown={(e) => {
        // Escape closes; Cmd/Ctrl-Enter saves from anywhere in the
        // form, including the notes box where plain Enter is a newline.
        if (e.key === 'Escape') { e.stopPropagation(); onCancel(); }
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void submit(); }
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <strong style={{ fontSize: 14 }}>{isEdit ? 'Edit block' : 'New block'}</strong>
        <button type="button" className="plan-btn plan-icon" onClick={onCancel} aria-label="Close">
          <X size={14} />
        </button>
      </div>

      <label style={{ display: 'block' }}>
        <span style={LABEL}>What is it?</span>
        <input
          ref={titleRef}
          className="plan-field"
          value={title}
          placeholder="Second shooter call"
          onChange={(e) => setTitle(e.target.value)}
        />
      </label>

      <label style={{ display: 'block' }}>
        <span style={LABEL}>Information</span>
        <textarea
          className="plan-field"
          value={description}
          placeholder="Anything you'll want in front of you on the day."
          onChange={(e) => setDescription(e.target.value)}
          style={{ height: 76, padding: '10px 12px', resize: 'vertical', lineHeight: 1.5 }}
        />
      </label>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
        <input
          type="checkbox"
          checked={allDay}
          onChange={(e) => {
            const on = e.target.checked;
            setAllDay(on);
            // Swapping the input type without re-deriving the value
            // leaves `datetime-local` text in a `date` field, which
            // every browser silently blanks.
            const s = start ? new Date(start.length <= 10 ? `${start}T00:00:00` : start) : draft.startAt;
            const en = end ? new Date(end.length <= 10 ? `${end}T00:00:00` : end) : draft.endAt;
            setStart(on ? localDate(s) : localInput(s));
            setEnd(on ? localDate(en) : localInput(en));
          }}
        />
        All day
      </label>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <label style={{ display: 'block', minWidth: 0 }}>
          <span style={LABEL}>Starts</span>
          <input
            className="plan-field"
            type={allDay ? 'date' : 'datetime-local'}
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </label>
        <label style={{ display: 'block', minWidth: 0 }}>
          <span style={LABEL}>Ends</span>
          <input
            className="plan-field"
            type={allDay ? 'date' : 'datetime-local'}
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </label>
      </div>

      <label style={{ display: 'block' }}>
        <span style={LABEL}>Where</span>
        <input
          className="plan-field"
          value={location}
          placeholder="Optional"
          onChange={(e) => setLocation(e.target.value)}
        />
      </label>

      {linkRow('project', projectId, setProjectId, sortedProjects)}
      {linkRow('client', clientId, setClientId, sortedClients)}

      <div style={{ display: 'flex', gap: 8, paddingTop: 2 }}>
        <button type="button" className="plan-primary" style={{ flex: 1 }} onClick={() => void submit()} disabled={saving}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : isEdit ? 'Save' : 'Add block'}
        </button>
        {isEdit && onDelete && (
          <button
            type="button"
            className="plan-btn plan-icon"
            aria-label="Delete block"
            title="Delete block"
            onClick={() => void onDelete(draft.id!)}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--muted)',
  marginBottom: 5,
};
